import {
  type CanonicalIcd10Code,
  isExaminationEncounterIcd10Code,
  isFollowUpExaminationIcd10Code,
  isObservationForSuspectedConditionIcd10Code,
  isScreeningExaminationIcd10Code,
  isSymptomIcd10Code,
} from "@smartmed/codes";

export const INVALID_EXAMINATION_ENCOUNTER_REVIEW_FLAG_TEXT = "Invalid Z00-Z13 ICD-10 coding";

export type ExaminationEncounterCondition = "A" | "B" | "C" | "D" | "E";

export type ExaminationEncounterViolation = {
  condition: ExaminationEncounterCondition;
  message: string;
  offendingCodes: { code: CanonicalIcd10Code; index: number }[];
};

export type ExaminationEncounterWarning = {
  code: CanonicalIcd10Code;
  index: number;
  message: string;
};

type IndexedCode = { code: CanonicalIcd10Code; index: number };

/**
 * One entry per position in the coder's list, in order, with `undefined` where the entry is not a
 * code at all — a reviewer mid-keystroke, or a feed that sent something else. The holes matter:
 * conditions B and D are about which position a code sits in, so dropping an entry would promote
 * the one after it.
 */
export type OrderedIcd10Codes = readonly (CanonicalIcd10Code | undefined)[];

/** Jigsaw coding practice: the only Z00-Z13 codes we ever assign. */
const isAssignableExaminationEncounterCode = (code: string) =>
  isObservationForSuspectedConditionIcd10Code(code) ||
  isFollowUpExaminationIcd10Code(code) ||
  isScreeningExaminationIcd10Code(code);

/** Jigsaw coding practice: Z03.8, Z12 and Z13 record why the episode happened, so nothing precedes them. */
const isPrimaryOnlyExaminationEncounterCode = (code: string) =>
  isObservationForSuspectedConditionIcd10Code(code) || isScreeningExaminationIcd10Code(code);

const codeList = (codes: readonly IndexedCode[]) => codes.map(({ code }) => code).join(", ");

const violation = (
  condition: ExaminationEncounterCondition,
  message: string,
  offendingCodes: readonly IndexedCode[],
): ExaminationEncounterViolation | null =>
  offendingCodes.length > 0 ? { condition, message, offendingCodes: [...offendingCodes] } : null;

export const findExaminationEncounterViolations = (
  orderedIcd10Codes: OrderedIcd10Codes,
): ExaminationEncounterViolation[] => {
  const codes = orderedIcd10Codes.flatMap((code, index) => (code === undefined ? [] : [{ code, index }]));
  const examinationCodes = codes.filter(({ code }) => isExaminationEncounterIcd10Code(code));
  const assignableCodes = examinationCodes.filter(({ code }) => isAssignableExaminationEncounterCode(code));

  const outsideAssignableSet = examinationCodes.filter(({ code }) => !isAssignableExaminationEncounterCode(code));
  const primaryOnlyCodesInSecondary = assignableCodes.filter(
    ({ code, index }) => isPrimaryOnlyExaminationEncounterCode(code) && index !== 0,
  );
  const primaryOnlyCodeWithSymptomCode =
    codes.some(({ code }) => isSymptomIcd10Code(code)) && assignableCodes[0]?.index === 0
      ? assignableCodes.slice(0, 1).filter(({ code }) => isPrimaryOnlyExaminationEncounterCode(code))
      : [];
  const followUpCodesTooLow = assignableCodes.filter(
    ({ code, index }) => isFollowUpExaminationIcd10Code(code) && index >= 2,
  );
  const duplicateAssignableCodes = assignableCodes.length > 1 ? assignableCodes : [];

  // DCS.XXI.1 governs Z00-Z13 primary use; the assignable set and positions are Jigsaw coding practice.
  return [
    violation("A", `Z00-Z13 code outside the allowed set: ${codeList(outsideAssignableSet)}`, outsideAssignableSet),
    violation(
      "B",
      `Z03.8, Z12 and Z13 codes must be primary: ${codeList(primaryOnlyCodesInSecondary)}`,
      primaryOnlyCodesInSecondary,
    ),
    violation(
      "C",
      `${codeList(primaryOnlyCodeWithSymptomCode)} cannot be primary when an R-code symptom or abnormal finding is present`,
      primaryOnlyCodeWithSymptomCode,
    ),
    violation(
      "D",
      `Z08 and Z09 codes may only appear in position 1 or 2: ${codeList(followUpCodesTooLow)}`,
      followUpCodesTooLow,
    ),
    violation(
      "E",
      `Only one allowed Z00-Z13 code may be assigned: ${codeList(duplicateAssignableCodes)}`,
      duplicateAssignableCodes,
    ),
  ].filter((item): item is ExaminationEncounterViolation => item !== null);
};

export const formatExaminationEncounterViolationDetails = (violations: readonly ExaminationEncounterViolation[]) =>
  violations.map((item) => `Condition ${item.condition}: ${item.message}`).join("; ");

export const formatExaminationEncounterReviewText = (violations: readonly ExaminationEncounterViolation[]) =>
  `${INVALID_EXAMINATION_ENCOUNTER_REVIEW_FLAG_TEXT}: ${formatExaminationEncounterViolationDetails(violations)}`;

export const examinationEncounterWarnings = (orderedIcd10Codes: OrderedIcd10Codes): ExaminationEncounterWarning[] => {
  const violations = findExaminationEncounterViolations(orderedIcd10Codes);
  const messagesByIndex = new Map<number, { code: CanonicalIcd10Code; messages: Set<string> }>();

  for (const violation of violations) {
    for (const offendingCode of violation.offendingCodes) {
      const existing = messagesByIndex.get(offendingCode.index) ?? {
        code: offendingCode.code,
        messages: new Set<string>(),
      };
      existing.messages.add(`Condition ${violation.condition}: ${violation.message}`);
      messagesByIndex.set(offendingCode.index, existing);
    }
  }

  return Array.from(messagesByIndex.entries())
    .sort(([left], [right]) => left - right)
    .map(([index, { code, messages }]) => ({
      code,
      index,
      message: `${INVALID_EXAMINATION_ENCOUNTER_REVIEW_FLAG_TEXT}: ${Array.from(messages).join("; ")}`,
    }));
};
